import { ActivatedRoute, ParamMap } from '@angular/router';
import { IBaseModelConstructor } from '@dev-stream/utils';

export interface IActivatedRouteInjected {
  route: ActivatedRoute;
}

export function FromRouteData<T = any>(
  dataKey: string = null,
  routeKey: string = 'route',
  ctr: IBaseModelConstructor<T> = null
) {
  return (target: any, propertyName: string) => {
    if (dataKey == null) {
      dataKey = propertyName;
    }
    Object.defineProperty(target, propertyName, {
      configurable: true,
      get() {
        const route = this[routeKey] as ActivatedRoute;
        if (route == null) {
          console.error(
            `Property ${routeKey} is not "ActivatedRoute". Maybe you forgot to inject it`
          );
        }
        const data = route.snapshot.data;
        if (data != null && dataKey in data) {
          if (ctr != null) {
            return new ctr(data[dataKey]);
          } else {
            return data[dataKey];
          }
        } else {
          return null;
        }
      },
      set() {
        throw new Error('Property is readonly');
      }
    });
  };
}

export function MapRouteParams<T = any>(
  property: string = null,
  routeKey: string = 'route',
  ctr: IBaseModelConstructor<T> | 'Array' = null
) {
  return MapParams(route => route.snapshot.paramMap, property, routeKey, ctr);
}

export function MapQueryParams<T = any>(
  property: string = null,
  routeKey: string = 'route',
  ctr: IBaseModelConstructor<T> | 'Array' = null
) {
  return MapParams(
    route => route.snapshot.queryParamMap,
    property,
    routeKey,
    ctr
  );
}

export function MapParams<T = any>(
  getParamMap: (route: ActivatedRoute) => ParamMap,
  property: string = null,
  routeKey: string = 'route',
  ctr: IBaseModelConstructor<T> | 'Array' = null
) {
  return (target: any, propertyName: string) => {
    Object.defineProperty(target, propertyName, {
      configurable: true,
      get() {
        const route = this[routeKey] as ActivatedRoute;
        if (route == null) {
          console.error(
            `Property ${routeKey} is not "ActivatedRoute". Maybe you forgot to inject it`
          );
        }
        const paramMap = getParamMap(route);

        if (paramMap != null) {
          if (property == null) {
            const obj = paramMap.keys.reduce((prev, key) => {
              return {
                ...prev,
                ...{ [key]: paramMap.get(key) }
              };
            }, {});
            if (ctr == null) {
              return obj;
            } else if (ctr === 'Array') {
              throw new Error(
                'Field "property" should be defined to take array from params'
              );
            } else {
              return new ctr(obj);
            }
          }
          if (!paramMap.has(property) == null) {
            return null;
          }
          const value = paramMap.get(property);
          if (ctr == null) {
            return value;
          } else if (ctr === 'Array') {
            return JSON.parse(`[${value}]`);
          } else {
            return new ctr(value as any);
          }
        }
      },
      set() {
        throw new Error('Property is readonly');
      }
    });
  };
}
